import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle, MessageSquare, Download, MapPin } from "lucide-react";
import { formatApiError } from "../../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

const PRIORITY_STYLE = {
  CRITICAL: "bg-[#EF4444]/10 text-[#EF4444]",
  HIGH: "bg-[#F97316]/10 text-[#F97316]",
  MEDIUM: "bg-[#F5B300]/10 text-[#F5B300]",
  LOW: "bg-white/5 text-slate-400",
};

const STATUS_STYLE = {
  OPEN: "bg-[#EF4444]/10 text-[#EF4444]",
  IN_PROGRESS: "bg-[#F5B300]/10 text-[#F5B300]",
  RESOLVED: "bg-[#22C55E]/10 text-[#22C55E]",
  CLOSED: "bg-white/5 text-slate-500",
};

export const TicketConsole = () => {
  const [tickets, setTickets] = useState(null);
  const [active, setActive] = useState(null);
  const [replies, setReplies] = useState([]);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios.get(`${API}/admin/enterprise/tickets`, headers()).then((r) => setTickets(r.data.tickets)).catch(() => setTickets([]));
  };
  useEffect(load, []);

  const openTicket = async (t) => {
    setActive(t);
    const { data } = await axios.get(`${API}/admin/enterprise/tickets/${t.id}/replies`, headers());
    setReplies(data.replies);
  };

  const sendReply = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/enterprise/tickets/${active.id}/reply`, { message: reply }, headers());
      toast.success("Reply sent");
      setReply("");
      openTicket(active);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/enterprise/tickets/${id}`, { status }, headers());
      toast.success(`Ticket ${status.toLowerCase().replace("_", " ")}`);
      load();
      if (active?.id === id) setActive({ ...active, status });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (tickets === null) return <LoaderCircle className="mt-6 h-5 w-5 animate-spin text-[#00D4FF]" />;
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="ticket-console">
      <p className="font-display text-lg font-bold">🎫 Support Ticket Console ({tickets.filter((t) => t.status === "OPEN").length} open)</p>
      <div className="mt-2 flex items-center justify-between">
        <p className="text-xs text-slate-500">SLA: Critical 4h · High 24h · Medium 48h · Low 72h</p>
        <button data-testid="escalate-breached" onClick={async () => {
          try {
            const { data } = await axios.post(`${API}/admin/enterprise/tickets/escalate-breached`, {}, headers());
            toast.success(`${data.escalated} tickets escalated`);
            load();
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
          }
        }} className="rounded-full border border-[#EF4444]/40 px-3 py-1.5 font-jbmono text-[10px] font-bold uppercase tracking-wider text-[#EF4444]">
          Escalate breached
        </button>
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div className="space-y-2.5">
          {tickets.map((t) => (
            <button key={t.id} data-testid={`ticket-${t.id.slice(-6)}`} onClick={() => openTicket(t)}
              className={`w-full rounded-2xl border p-4 text-left transition-all ${active?.id === t.id ? "border-[#00D4FF]/50 bg-[#00D4FF]/5" : "border-white/10 bg-[#09090B]/80 hover:border-white/20"}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="truncate text-sm font-bold">{t.subject}</p>
                <span className={`rounded-full px-2.5 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider ${STATUS_STYLE[t.status]}`}>{t.status}</span>
              </div>
              <p className="mt-1 font-jbmono text-[10px] text-slate-500">{t.user_email} · {t.priority}{t.sla_breached ? " · 🚨 SLA breached" : ""}</p>
              <p className="mt-0.5 font-jbmono text-[9px] text-slate-600">SLA: {String(t.sla_deadline).slice(0, 16).replace("T", " ")}</p>
            </button>
          ))}
        </div>
        {active && (
          <div className="rounded-2xl border border-white/10 bg-[#09090B]/80 p-5" data-testid="ticket-detail">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-display font-bold">{active.subject}</p>
                <p className="mt-1 text-xs text-slate-500">{active.user_email}</p>
              </div>
              <span className={`rounded-full px-2.5 py-1 font-jbmono text-[9px] font-bold uppercase ${PRIORITY_STYLE[active.priority]}`}>{active.priority}</span>
            </div>
            <p className="mt-3 text-sm text-slate-300">{active.description}</p>
            <div className="mt-4 flex gap-2">
              {["IN_PROGRESS", "RESOLVED", "CLOSED"].map((s) => (
                <button key={s} data-testid={`ticket-${s.toLowerCase()}`} onClick={() => setStatus(active.id, s)}
                  className="rounded-full border border-white/10 px-3.5 py-1.5 font-jbmono text-[10px] font-bold uppercase text-slate-400 hover:text-white">
                  {s.replace("_", " ")}
                </button>
              ))}
            </div>
            <div className="mt-5 border-t border-white/10 pt-4">
              <p className="flex items-center gap-2 font-display text-sm font-bold"><MessageSquare className="h-4 w-4 text-[#00D4FF]" /> Replies ({replies.length})</p>
              <div className="mt-3 space-y-2">
                {replies.map((r) => (
                  <div key={r.id} className="rounded-xl bg-white/5 p-3">
                    <p className="text-xs font-bold">{r.admin_name}</p>
                    <p className="mt-1 text-xs text-slate-400">{r.message}</p>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <input data-testid="ticket-reply" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply to customer…"
                  className="flex-1 rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm outline-none focus:border-[#00D4FF]/60" />
                <button data-testid="ticket-send" onClick={sendReply} disabled={busy || !reply.trim()}
                  className="rounded-full bg-gradient-to-r from-[#00D4FF] to-[#7B61FF] px-5 py-2.5 font-display text-xs font-bold uppercase text-white disabled:opacity-50">
                  {busy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : "Send"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export const SessionTracker = () => {
  const [sessions, setSessions] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/enterprise/sessions`, headers()).then((r) => setSessions(r.data.sessions)).catch(() => setSessions([]));
  }, []);

  if (sessions === null) return null;
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="session-tracker">
      <p className="flex items-center gap-2 font-display text-lg font-bold"><MapPin className="h-5 w-5 text-[#00D4FF]" /> Admin Sessions ({sessions.length})</p>
      <p className="mt-1 text-xs text-slate-500">Live admin logins with device, IP and location.</p>
      {sessions.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">No admin sessions recorded yet.</p>
      ) : (
        <div className="mt-4 space-y-2">
          {sessions.map((s) => (
            <div key={s.id} data-testid={`session-${s.id.slice(-6)}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-[#09090B]/80 p-4">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold">{s.admin_id.slice(-8)}</p>
                <p className="truncate font-jbmono text-[10px] text-slate-500">{s.ip_address} · {s.browser_info.slice(0, 50)}</p>
              </div>
              <span className="font-jbmono text-[9px] text-slate-600">{String(s.login_time).slice(0, 16).replace("T", " ")}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export const DataExport = () => {
  const [busy, setBusy] = useState(null);
  const download = async (dataset) => {
    setBusy(dataset);
    try {
      const res = await axios.get(`${API}/admin/enterprise/export/${dataset}?format=csv`, { ...headers(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${dataset}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${dataset} exported`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };
  const datasets = ["admins", "roles", "departments", "permissions", "activity_logs", "countries", "languages"];
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="data-export">
      <p className="flex items-center gap-2 font-display text-lg font-bold"><Download className="h-5 w-5 text-[#00FFD1]" /> Data Export</p>
      <p className="mt-1 text-xs text-slate-500">Download CSV exports for compliance and audits.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {datasets.map((d) => (
          <button key={d} data-testid={`export-${d}`} onClick={() => download(d)} disabled={busy === d}
            className="rounded-full border border-white/10 px-4 py-2 font-jbmono text-[10px] font-bold uppercase tracking-wider text-slate-300 hover:border-[#00D4FF]/60 hover:text-white disabled:opacity-50">
            {busy === d ? <LoaderCircle className="h-3 w-3 animate-spin" /> : d}
          </button>
        ))}
      </div>
    </section>
  );
};
